import { useState, useEffect, useRef, useLayoutEffect, type KeyboardEvent, type ReactNode } from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

import { Card } from '@components/common/Card';
import { Container } from '@components/common/Container';
import { Icon } from '@components/Icon';
import { LandingCourseDetailModal } from '@components/features/landing/LandingCourseDetailModal';
import { CLASS_IMAGE_PLACEHOLDER } from '@constants/classImage';
import { gradeLevels } from '@constants/landingCourses';
import { buildClassName } from '@utils';

import type { Course, GradeLevel } from '../../../types/courses';

/** Tablet portrait (768–1024): 2.5 cards, advance by 2; tablet landscape (1025–1365): ~3.33 cards, advance by 3; desktop (1366+): 3, advance by 3 */
function useCardsPerView(): number {
  const [cardsPerView, setCardsPerView] = useState(3);
  useEffect(() => {
    const portrait = window.matchMedia('(min-width: 640px) and (max-width: 1024px)');
    const landscape = window.matchMedia('(min-width: 1025px) and (max-width: 1365px)');
    const handler = () => {
      if (portrait.matches) setCardsPerView(2);
      else if (landscape.matches) setCardsPerView(3);
      else setCardsPerView(3);
    };
    handler();
    portrait.addEventListener('change', handler);
    landscape.addEventListener('change', handler);
    return () => {
      portrait.removeEventListener('change', handler);
      landscape.removeEventListener('change', handler);
    };
  }, []);
  return cardsPerView;
}

/** Visible track area inside the carousel overflow container (excludes horizontal padding). */
function getCarouselVisibleWidth(container: HTMLElement): number {
  const style = window.getComputedStyle(container);
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const paddingRight = parseFloat(style.paddingRight) || 0;
  return container.clientWidth - paddingLeft - paddingRight;
}

/** Pixel translate for the track: last page scrolls flush so the final card is fully visible. */
function computeCarouselTranslate(
  container: HTMLElement,
  track: HTMLElement,
  currentIndex: number,
  maxIndex: number,
): number {
  const slides = Array.from(track.children) as HTMLElement[];
  if (slides.length === 0) return 0;

  const visibleWidth = getCarouselVisibleWidth(container);
  const trackWidth = track.scrollWidth;
  const maxScroll = Math.max(0, trackWidth - visibleWidth);

  if (currentIndex >= maxIndex) {
    return -maxScroll;
  }

  const slide = slides[Math.min(currentIndex, slides.length - 1)];
  if (!slide) return 0;

  return -Math.min(slide.offsetLeft, maxScroll);
}

export interface CurrentCoursesSectionProps {
  semesterLabel: string;
  /** When true, show skeleton for semester badge instead of label text. */
  semesterLabelLoading?: boolean;
  /** Approved classes for the active semester (empty while loading or when none). */
  courses?: Course[];
  /** True while classes are being fetched; show loading state instead of empty. */
  loading?: boolean;
  /** True when an active semester exists. Controls the empty-state message. */
  hasActiveSemester?: boolean;
}

export function CurrentCoursesSection({
  semesterLabel,
  semesterLabelLoading = false,
  courses: coursesProp = [],
  loading = false,
  hasActiveSemester = false,
}: CurrentCoursesSectionProps) {
  const [selectedGradeLevel, setSelectedGradeLevel] = useState<GradeLevel>('All');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [currentCourseIndex, setCurrentCourseIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const cardsPerView = useCardsPerView();

  const courseList = coursesProp;
  const filteredCourses =
    selectedGradeLevel === 'All'
      ? courseList
      : courseList.filter((c) => c.gradeLevel === selectedGradeLevel);

  const maxIndex = Math.max(
    0,
    Math.floor((filteredCourses.length - 1) / cardsPerView) * cardsPerView
  );

  /** Changing the grade filter starts again at the first page with no course open. */
  const handleSelectGradeLevel = (level: GradeLevel) => {
    if (level === selectedGradeLevel) return;
    setSelectedGradeLevel(level);
    setCurrentCourseIndex(0);
    setSelectedCourse(null);
  };

  /** Clamp index when maxIndex shrinks (e.g. filter change or breakpoint) so we don't get stuck past last page */
  const [prevMaxIndex, setPrevMaxIndex] = useState(maxIndex);
  if (prevMaxIndex !== maxIndex) {
    setPrevMaxIndex(maxIndex);
    if (currentCourseIndex > maxIndex) setCurrentCourseIndex(maxIndex);
  }

  /** Pixel-based translate. On last page: scroll to end so the last card is fully in view. */
  useLayoutEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    if (!container || !track || filteredCourses.length === 0) return;

    track.style.transform = `translateX(${computeCarouselTranslate(container, track, currentCourseIndex, maxIndex)}px)`;
  }, [currentCourseIndex, maxIndex, filteredCourses.length]);

  /** Recompute translate on container or track resize (orientation, breakpoint, or content e.g. images loading) */
  useLayoutEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    if (!container || !track) return;

    const updateTransform = () => {
      const c = containerRef.current;
      const t = trackRef.current;
      if (!c || !t || filteredCourses.length === 0) return;
      t.style.transform = `translateX(${computeCarouselTranslate(c, t, currentCourseIndex, maxIndex)}px)`;
    };

    const resizeObserver = new ResizeObserver(updateTransform);
    resizeObserver.observe(container);
    resizeObserver.observe(track);
    return () => resizeObserver.disconnect();
  }, [currentCourseIndex, maxIndex, filteredCourses.length]);

  const handlePrevious = () => {
    setCurrentCourseIndex((prev) => {
      const newIndex = prev - cardsPerView;
      return newIndex < 0 ? maxIndex : newIndex;
    });
  };

  const handleNext = () => {
    setCurrentCourseIndex((prev) => {
      const newIndex = prev + cardsPerView;
      return newIndex > maxIndex ? 0 : newIndex;
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart == null || touchEnd == null) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) handleNext();
    if (distance < -50) handlePrevious();
  };

  let body: ReactNode;
  if (loading && courseList.length === 0) {
    body = (
      <div className="bhg-courses-carousel-wrapper" aria-busy="true" aria-label="Loading classes">
        <div className="bhg-courses-carousel">
          <button
            type="button"
            className="bhg-courses-carousel__arrow bhg-courses-carousel__arrow--prev"
            aria-label="Previous courses"
            disabled
          >
            <IconChevronLeft
              className="bhg-courses-carousel__arrow-icon"
              size={28}
              strokeWidth={2.5}
            />
          </button>
          <div className="bhg-courses-carousel__container">
            <div className="bhg-courses-carousel__track">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bhg-courses-carousel__slide">
                  <CourseCardSkeleton />
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="bhg-courses-carousel__arrow bhg-courses-carousel__arrow--next"
            aria-label="Next courses"
            disabled
          >
            <IconChevronRight
              className="bhg-courses-carousel__arrow-icon"
              size={28}
              strokeWidth={2.5}
            />
          </button>
          <div
            className="bhg-courses-carousel__indicators bhg-courses-carousel__indicators--hidden"
            aria-hidden
          >
            <span className="bhg-courses-carousel__indicator bhg-courses-carousel__indicator--active" aria-hidden />
          </div>
        </div>
      </div>
    );
  } else if (filteredCourses.length > 0) {
    body = (
      <div className="bhg-courses-carousel-wrapper">
        <div className="bhg-courses-carousel">
        <button
          type="button"
          className="bhg-courses-carousel__arrow bhg-courses-carousel__arrow--prev"
          onClick={handlePrevious}
          aria-label="Previous courses"
          disabled={
            filteredCourses.length <= cardsPerView || currentCourseIndex === 0
          }
        >
          <IconChevronLeft
            className="bhg-courses-carousel__arrow-icon"
            size={28}
            strokeWidth={2.5}
          />
        </button>

        <div
          ref={containerRef}
          className="bhg-courses-carousel__container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div ref={trackRef} className="bhg-courses-carousel__track">
            {filteredCourses.map((course) => (
              <div key={course.id} className="bhg-courses-carousel__slide">
                <CourseCard
                  course={course}
                  isSelected={selectedCourse?.id === course.id}
                  onSelect={() => setSelectedCourse(course)}
                />
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="bhg-courses-carousel__arrow bhg-courses-carousel__arrow--next"
          onClick={handleNext}
          aria-label="Next courses"
          disabled={
            filteredCourses.length <= cardsPerView || currentCourseIndex >= maxIndex
          }
        >
          <IconChevronRight
            className="bhg-courses-carousel__arrow-icon"
            size={28}
            strokeWidth={2.5}
          />
        </button>

        <div
          className={`bhg-courses-carousel__indicators ${filteredCourses.length <= cardsPerView ? 'bhg-courses-carousel__indicators--hidden' : ''}`}
          aria-hidden={filteredCourses.length <= cardsPerView}
        >
          {Array.from({
            length: Math.max(1, Math.ceil(filteredCourses.length / cardsPerView)),
          }).map((_, index) => (
            <button
              key={index}
              type="button"
              className={`bhg-courses-carousel__indicator ${Math.floor(currentCourseIndex / cardsPerView) === index ? 'bhg-courses-carousel__indicator--active' : ''}`}
              onClick={() => setCurrentCourseIndex(index * cardsPerView)}
              aria-label={`Go to page ${index + 1}`}
              disabled={filteredCourses.length <= cardsPerView}
            />
          ))}
        </div>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="bhg-courses-empty">
        <Card elevation={2} className="bhg-courses-empty__card">
          <Icon
            type="calendar"
            style="outlined"
            colorVariant="blue"
            size="xl"
            aria-label=""
          />
          <p role="status">
            {coursesEmptyMessage(selectedGradeLevel, hasActiveSemester)}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <section id="courses" className="bhg-landing-section">
      <div className="bhg-landing-section__background">
        <div className="bhg-landing-section__overlay" />
      </div>
      <Container size="lg">
        <div className="bhg-landing-section__header">
          <SemesterBadge label={semesterLabel} loading={semesterLabelLoading} />
          <h2 id="courses-heading" className="bhg-landing-section__title">Current Courses</h2>
          <p className="bhg-landing-section__description">
            Browse our wide selection of academic and enrichment classes offered
            each semester. Use the filters below, then select a class to view time,
            price, and full description.{' '}
            <span className="bhg-landing-courses__intro-desktop">
              Swipe or use arrows to browse; click a class for details.
            </span>
            <span className="bhg-landing-courses__intro-mobile">
              Scroll to see all classes; tap a class for details.
            </span>
          </p>
        </div>

        <div className="bhg-courses-filters">
          {gradeLevels.map((level) => (
            <button
              key={level}
              type="button"
              className={`bhg-courses-filter ${selectedGradeLevel === level ? 'bhg-courses-filter--active' : ''}`}
              onClick={() => handleSelectGradeLevel(level)}
            >
              {level}
            </button>
          ))}
        </div>

        {body}

        <LandingCourseDetailModal
          course={selectedCourse}
          isOpen={selectedCourse != null}
          onClose={() => setSelectedCourse(null)}
        />
      </Container>
    </section>
  );
}

function coursesEmptyMessage(selectedGradeLevel: GradeLevel, hasActiveSemester: boolean): string {
  if (selectedGradeLevel !== 'All') return 'No courses found for this grade level.';
  if (hasActiveSemester) return 'No classes this semester.';
  return 'Classes have not been posted yet.';
}

function SemesterBadge({ label, loading }: { label: string; loading: boolean }) {
  if (loading) {
    return (
      <span
        className="bhg-landing-section__semester bhg-landing-section__semester--skeleton"
        aria-busy="true"
        aria-label="Loading semester"
      />
    );
  }
  if (!label) return null;
  return <span className="bhg-landing-section__semester">{label}</span>;
}

function CourseCardSkeleton() {
  return (
    <Card elevation={3} className="bhg-course-carousel-card bhg-course-carousel-card--skeleton">
      <div className="bhg-course-carousel-card__image-wrapper">
        <div className="bhg-course-carousel-card__skeleton-image" aria-hidden />
        <div className="bhg-course-carousel-card__skeleton-badge" aria-hidden />
      </div>
      <div className="bhg-course-carousel-card__content">
        <div className="bhg-course-carousel-card__skeleton-line bhg-course-carousel-card__skeleton-line--title" aria-hidden />
        <div className="bhg-course-carousel-card__skeleton-line bhg-course-carousel-card__skeleton-line--time" aria-hidden />
        <div className="bhg-course-carousel-card__skeleton-line bhg-course-carousel-card__skeleton-line--desc" aria-hidden />
        <div className="bhg-course-carousel-card__skeleton-line bhg-course-carousel-card__skeleton-line--desc bhg-course-carousel-card__skeleton-line--short" aria-hidden />
      </div>
    </Card>
  );
}

const GRADE_BADGE_LABEL: Record<Course['gradeLevel'], string> = {
  All: 'All',
  Preschool: 'Preschool',
  Elementary: 'Elementary',
  Middle: 'Middle School',
  'High School': 'High School',
  Adult: 'Adult',
};

function CourseCard({
  course,
  isSelected,
  onSelect,
}: {
  course: Course;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <Card
      elevation={3}
      interactive
      padding={false}
      className={buildClassName(
        'bhg-course-carousel-card',
        'bhg-course-carousel-card--selectable',
        isSelected && 'bhg-course-carousel-card--selected'
      )}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${course.name}`}
      aria-pressed={isSelected}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
    >
      <div className="bhg-course-carousel-card__image-wrapper">
        <img
          src={course.image || CLASS_IMAGE_PLACEHOLDER}
          alt=""
          className="bhg-course-carousel-card__image"
          loading="lazy"
          width={320}
          height={180}
          onError={(e) => {
            const el = e.currentTarget;
            if (el.src !== CLASS_IMAGE_PLACEHOLDER) {
              el.src = CLASS_IMAGE_PLACEHOLDER;
              el.onerror = null;
            }
          }}
        />
        <div className="bhg-course-carousel-card__badge">
          {GRADE_BADGE_LABEL[course.gradeLevel] ?? course.gradeLevel}
        </div>
      </div>
      <div className="bhg-course-carousel-card__content">
        <h3 className="bhg-course-carousel-card__name">{course.name}</h3>
        {(course.time || course.priceLabel) && (
          <div className="bhg-course-carousel-card__meta">
            {course.time ? (
              <span className="bhg-course-carousel-card__time">{course.time}</span>
            ) : null}
            {course.priceLabel ? (
              <span className="bhg-course-carousel-card__price">{course.priceLabel}</span>
            ) : null}
          </div>
        )}
        {course.description ? (
          <p className="bhg-course-carousel-card__description">{course.description}</p>
        ) : null}
        <span className="bhg-course-carousel-card__cta" aria-hidden>
          View details
        </span>
      </div>
    </Card>
  );
}
